import React, { useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

import { useAppSelector, useAppDispatch } from "@APP/redux";
import { PageLayout, ErrorMessage, CardPaymentButton } from "@APP/components";
import { setSelectedBank } from "@APP/redux/actions";
import { SCREEN_PATHS } from "@APP/navigation";
import CONFIG from "@APP/config";
import { MOBILE_CARD_RADIUS, MOBILE_FONT, MOBILE_TAP_TARGET } from "@APP/styles/mobile";

const PAGE_SIZE = 15;

const useStyles = makeStyles((theme) => {
  const LIST_ITEM_V_HEIGHT = 7;
  return {
    list: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      overflowY: "auto",
      overflowX: "hidden",
      padding: 0,
      [theme.breakpoints.down("sm")]: {
        gap: theme.spacing(1),
      },
      "&::-webkit-scrollbar": {
        width: 10,
        backgroundColor: theme.palette.action.hover,
      },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.action.active,
      },
    },
    listItem: {
      minHeight: `${LIST_ITEM_V_HEIGHT}vh`,
      [theme.breakpoints.down("sm")]: {
        minHeight: 48,
        borderRadius: MOBILE_CARD_RADIUS,
        "& .MuiListItemText-primary": {
          fontSize: MOBILE_FONT.body,
        },
      },
      backgroundColor: theme.palette.background.paper,
      borderRadius: 12,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      // The avatar slot reserves 56px by default, which leaves a large gap
      // next to the small brand mark the design uses.
      "& .MuiListItemAvatar-root": {
        minWidth: "auto",
        display: "flex",
      },
    },
    logoTile: {
      // The design uses a small bank mark, roughly a third of the row height.
      width: "1.75rem",
      height: "1.75rem",
      [theme.breakpoints.down("sm")]: {
        width: 24,
        height: 24,
      },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      backgroundColor: theme.palette.background.paper,
      marginRight: theme.spacing(2),
    },
    icon: {
      objectFit: "contain",
      width: "80%",
      height: "80%",
    },
    chevron: {
      color: theme.palette.text.primary,
    },
    // Sits under the list, aligned to the page gutter rather than the card
    // inset — the design hangs it outside the white rows.
    notListed: {
      marginTop: theme.spacing(3),
      color: theme.palette.text.primary,
      [theme.breakpoints.down("sm")]: {
        marginTop: theme.spacing(2),
        fontSize: MOBILE_FONT.body,
      },
    },
    previousLink: {
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      cursor: "pointer",
      color: theme.palette.text.primary,
      background: "none",
      border: "none",
      padding: 0,
      [theme.breakpoints.down("sm")]: {
        // A bare text link is too small to tap comfortably, so the link takes
        // a full 44px hit area. The icon is pulled back by its own optical
        // side bearing so the arrow — not the touch padding — still lines up
        // with the page gutter and the headline below it.
        minHeight: MOBILE_TAP_TARGET,
        marginLeft: -2,
        paddingRight: theme.spacing(1),
        "& .MuiTypography-root": {
          fontSize: MOBILE_FONT.body,
        },
      },
    },
    // -- Mobile -------------------------------------------------------------
    // MUI's default Accordion carries an elevation shadow and a top divider
    // pseudo-element; the design is a plain white card.
    helpAccordion: {
      marginTop: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      borderRadius: MOBILE_CARD_RADIUS,
      boxShadow: "none",
      "&:before": {
        display: "none",
      },
      "&.Mui-expanded": {
        margin: `${theme.spacing(2)} 0 0`,
      },
    },
    helpSummary: {
      minHeight: 48,
      "&.Mui-expanded": {
        minHeight: 48,
      },
      "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded": {
        margin: 0,
      },
    },
    helpDetails: {
      paddingTop: 0,
      fontSize: MOBILE_FONT.body,
      "& ul": {
        margin: 0,
        paddingLeft: theme.spacing(3),
      },
    },
  };
});

const BankSelectScreen: React.FC<RouteComponentProps> = ({ history }) => {
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const banksState = useAppSelector((state) => state.banks);
  const rtpState = useAppSelector((state) => state.rtp);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { error, banks, loading } = banksState;

  // The design names the requesting business in the "bank not listed" line; the
  // generic wording stands in when the sender details have not loaded.
  const senderName =
    rtpState.senderData?.businessContact?.name ??
    rtpState.senderData?.name ??
    "the business that sent you this request";

  const sortedBanks = React.useMemo(
    () =>
      // The API returns banks in an arbitrary order; the design lists them A-Z.
      [...(banks || [])].sort((a, b) =>
        a.fullName.localeCompare(b.fullName, undefined, { sensitivity: "base" }),
      ),
    [banks],
  );

  const visibleBanks = sortedBanks.slice(0, visibleCount);

  // Both mobile and desktop advance straight to confirmation when a bank row is
  // picked — the forward arrow on each row implies the action, so there is no
  // separate "Continue" step.
  const goToConfirm = (bankId: string) => {
    dispatch(setSelectedBank(bankId));
    history.push(SCREEN_PATHS.INITIATE_PAYMENT.replace(":bankId", bankId));
  };

  const renderMainContent = () => {
    if (error) {
      return (
        <Box display="flex" alignItems="center" flexDirection="column" id="bankSelectBox">
          <ErrorMessage />
        </Box>
      );
    }

    return (
      <>
        {/* The back link is shown on every breakpoint — on mobile it is the
            only way back through the journey, since there is no browser chrome
            inside the in-app webview. */}
        <Box width="100%" mb={isMobile ? 0.5 : "2vh"}>
          <button
            type="button"
            className={styles.previousLink}
            id="bankSelectPreviousLink"
            onClick={() => history.goBack()}>
            <ArrowBackIcon fontSize="small" />
            <Typography component="span">Previous</Typography>
          </button>
        </Box>
        <Box width="100%" mb={isMobile ? "20px" : 3}>
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontSize: { xs: `${MOBILE_FONT.headline}px`, sm: "30px" }, lineHeight: 1.2 }}
            id="bankSelectHeading">
            Select which account to pay from
          </Typography>
        </Box>
        <Box width="100%" maxWidth="600px">
          <List disablePadding className={styles.list} id="bankSelectListParent">
            {visibleBanks.length ? (
              visibleBanks.map((bankItem, index) => (
                <ListItem
                  className={styles.listItem}
                  button
                  id={"bankSelectListItem" + index}
                  key={bankItem.id}
                  onClick={() => goToConfirm(bankItem.id)}>
                  <ListItemAvatar>
                    <Box className={styles.logoTile}>
                      {bankItem.logo ? (
                        <img
                          className={styles.icon}
                          src={bankItem.logo}
                          id="bankSelectListItemBankLogo"
                          alt={`${bankItem.fullName} Logo`}
                        />
                      ) : (
                        <AccountBalanceIcon className={styles.icon} id="bankSelectAccountBalIcon" />
                      )}
                    </Box>
                  </ListItemAvatar>
                  <ListItemText
                    primary={bankItem.fullName}
                    id="bankSelectBankFullName"
                    primaryTypographyProps={{ color: "textPrimary" }}
                  />
                  <ListItemSecondaryAction>
                    {/* A forward arrow on each row — picking a bank advances
                        straight to confirmation. */}
                    <ArrowForwardIcon
                      fontSize="small"
                      className={styles.chevron}
                      id="bankSelectChevronRigthIcon"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))
            ) : (
              <Typography paddingTop={3} align="center" id="bankSelectNoBankResult">
                There are no banks available.
              </Typography>
            )}
          </List>
          {visibleCount < sortedBanks.length && (
            <Box display="flex" justifyContent="center" mt={isMobile ? 2 : 3}>
              <Button
                variant="outlined"
                color="secondary"
                id="bankSelectLoadMoreButton"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
                Load more
              </Button>
            </Box>
          )}
          <Typography className={styles.notListed} id="bankSelectBankNotListed">
            {t("Screens.BankSelect.bankNotListed", { senderName })}
          </Typography>
          {CONFIG?.FEATURE?.SQUARE && rtpState.rtp?.cardPaymentProvider ? (
            <Box mt={3}>
              <CardPaymentButton />
            </Box>
          ) : null}
        </Box>
      </>
    );
  };

  return (
    <PageLayout loading={loading}>
      {/* Left-aligned against the page gutter, matching the LBG design. */}
      <Box display="flex" alignItems="flex-start" flexDirection="column">
        {renderMainContent()}
      </Box>
    </PageLayout>
  );
};

export default BankSelectScreen;
